import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';
import { vi } from 'date-fns/locale';
import {
  Calendar,
  Download,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  BarChart3,
} from 'lucide-react';
import { QuickDateRange } from '../components/common/QuickDateRange';
import { DateRangePreset, getDateRangeForPreset } from '../utils/dateRangeUtils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ordersService } from '../services/ordersService';
import { useTranslation } from '../hooks/useTranslation';
import { getFiscalMonthRange } from '../stores/fiscalMonthSettingsStore';

// Modern UI Components
const Card = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-card text-card-foreground rounded-lg border shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardHeader = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
);

const CardTitle = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h3
    className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
  >
    {children}
  </h3>
);

const CardDescription = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p className={`text-sm text-muted-foreground ${className}`}>{children}</p>
);

const CardContent = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`p-6 pt-0 ${className}`}>{children}</div>;

const Button = ({
  children,
  onClick,
  disabled = false,
  variant = 'default',
  className = '',
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  className?: string;
  [key: string]: any;
}) => {
  const baseClasses =
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline:
      'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({
  type = 'text',
  value,
  onChange,
  placeholder,
  className = '',
  ...props
}: {
  type?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  [key: string]: any;
}) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Label = ({
  children,
  className = '',
  htmlFor,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
  [key: string]: any;
}) => (
  <label
    htmlFor={htmlFor}
    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
    {...props}
  >
    {children}
  </label>
);

const Badge = ({
  children,
  variant = 'default',
  className = '',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'secondary' | 'outline';
  className?: string;
}) => {
  const baseClasses =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

  const variantClasses = {
    default:
      'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
    secondary:
      'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'text-foreground',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </div>
  );
};

const Checkbox = ({
  checked,
  onChange,
  id,
  label,
  className = '',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  label: string;
  className?: string;
}) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
    />
    <label htmlFor={id} className="text-sm font-medium cursor-pointer">
      {label}
    </label>
  </div>
);


const Select = ({
  value,
  onChange,
  className = '',
  children,
  ...props
}: {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  children: React.ReactNode;
  [key: string]: any;
}) => (
  <select
    value={value}
    onChange={onChange}
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </select>
);

const ModernOrdersReport = () => {
  const { translate, locale } = useTranslation();
  const [selectedPreset, setSelectedPreset] =
    useState<DateRangePreset>('thisWeek');
  const [dateRange, setDateRange] = useState(() => {
    // Initialize with "this week" (Monday to Sunday)
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysToMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return {
      from: format(monday, 'yyyy-MM-dd'),
      to: format(sunday, 'yyyy-MM-dd'),
    };
  });
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const isAdjustingDateRange = useRef(false);
  
  // Platform filter state
  const [includedPlatforms, setIncludedPlatforms] = useState<Set<string>>(
    new Set(['internal', 'grab', 'shopee', 'xanhsm']) // Include all platforms by default
  );

  const handleFetchReport = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      console.log('Fetching orders report for date range:', dateRange);
      console.log('Included platforms:', Array.from(includedPlatforms));
      console.log(
        'API Root URL:',
        import.meta.env.VITE_API_ROOT || 'http://localhost:3001/api'
      );

      // Check if all platforms are included (default state)
      const allPlatforms = new Set(['internal', 'grab', 'shopee', 'xanhsm']);
      const allPlatformsSelected = 
        includedPlatforms.size === allPlatforms.size &&
        Array.from(includedPlatforms).every(platform => allPlatforms.has(platform));

      if (allPlatformsSelected) {
        // Use the original optimized endpoint when all platforms are selected
        // This fetches all orders directly from MongoDB without pagination limits
        const response = await ordersService.getOrdersReport({
          from: dateRange.from,
          to: dateRange.to,
        });

        console.log('Orders report response (all platforms):', response);
        setData(response);
      } else {
        // Fetch individual orders to filter by platform when filtering is needed
        // Parse date strings (YYYY-MM-DD) as local dates, not UTC
        // This ensures the date range matches what users expect in their timezone
        const [fromYear, fromMonth, fromDay] = dateRange.from.split('-').map(Number);
        const fromDate = new Date(fromYear, fromMonth - 1, fromDay, 0, 0, 0, 0);
        
        const [toYear, toMonth, toDay] = dateRange.to.split('-').map(Number);
        const toDate = new Date(toYear, toMonth - 1, toDay, 23, 59, 59, 999);

        // Fetch all orders by paginating through all pages
        let allOrders: any[] = [];
        let page = 1;
        const perPage = 10000;
        let hasMore = true;

        while (hasMore) {
          const ordersResponse = await ordersService.getOrders({
            pagination: { page, perPage },
            sort: { field: 'date', order: 'ASC' },
            filter: {
              dateFrom: fromDate.toISOString(),
              dateTo: toDate.toISOString(),
            },
          });

          allOrders = allOrders.concat(ordersResponse.data);

          // Check if we've fetched all orders
          if (ordersResponse.data.length < perPage || allOrders.length >= ordersResponse.total) {
            hasMore = false;
          } else {
            page++;
          }
        }

        console.log(`Fetched ${allOrders.length} total orders`);

        // Filter orders by platform
        // Orders without sourcePlatform (null, undefined, or empty string) are treated as 'internal' by default
        const filteredOrders = allOrders.filter((order: any) => {
          const orderPlatform = order.sourcePlatform;
          // Default to 'internal' if platform is null, undefined, or empty string
          const normalizedPlatform = 
            !orderPlatform || (typeof orderPlatform === 'string' && orderPlatform.trim() === '') || !['internal', 'grab', 'shopee', 'xanhsm'].includes(orderPlatform)
              ? 'internal'
              : orderPlatform;
          return includedPlatforms.has(normalizedPlatform);
        });

        console.log(`Filtered ${filteredOrders.length} orders from ${allOrders.length} total orders`);

        // Calculate report data from filtered orders
        const dailyTotals: { [key: string]: number } = {};
        const dailyOrderCounts: { [key: string]: number } = {};
        const dailyCancelledCounts: { [key: string]: number } = {};
        let periodTotal = 0;
        let totalDeliveredOrders = 0;
        let totalCancelledOrders = 0;

        filteredOrders.forEach((order: any) => {
          // Handle date field (can be string or Date object)
          // Parse date in local timezone to get correct date (not UTC)
          let orderDate: string | null = null;
          if (order.date) {
            let dateObj: Date;
            if (typeof order.date === 'string') {
              dateObj = new Date(order.date);
            } else {
              dateObj = new Date(order.date);
            }
            
            if (!isNaN(dateObj.getTime())) {
              // Format as YYYY-MM-DD in local timezone (not UTC)
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              orderDate = `${year}-${month}-${day}`;
            }
          }
          if (!orderDate) return;

          // Calculate total from items if total field doesn't exist or is 0
          let orderTotal = order.total || 0;
          if (orderTotal === 0 && order.items && Array.isArray(order.items) && order.items.length > 0) {
            orderTotal = order.items.reduce((sum: number, item: any) => {
              const itemTotal = (item.price || 0) * (item.quantity || 0);
              return sum + itemTotal;
            }, 0);
          }

          const isDelivered = order.status === 'delivered';
          const isCancelled = order.status === 'cancelled';

          if (isDelivered) {
            dailyTotals[orderDate] = (dailyTotals[orderDate] || 0) + orderTotal;
            dailyOrderCounts[orderDate] = (dailyOrderCounts[orderDate] || 0) + 1;
            periodTotal += orderTotal;
            totalDeliveredOrders += 1;
          } else if (isCancelled) {
            dailyCancelledCounts[orderDate] = (dailyCancelledCounts[orderDate] || 0) + 1;
            totalCancelledOrders += 1;
          }
        });

        const response = {
          periodTotal,
          totalDeliveredOrders,
          totalCancelledOrders,
          totalOrders: totalDeliveredOrders + totalCancelledOrders,
          dailyTotals,
          dailyOrderCounts,
          dailyCancelledCounts,
        };

        console.log('Calculated report data (filtered):', response);
        setData(response);
      }
    } catch (error) {
      setIsError(true);
      console.error('Error fetching orders report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPreset('custom');
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value,
    });
  };

  const handleRangeChange = (from: string, to: string, preset: DateRangePreset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setDateRange({ from, to });
    }
  };

  // Auto-fetch data when component mounts or date range/platform filter changes
  useEffect(() => {
    if (dateRange.from && dateRange.to && !isAdjustingDateRange.current) {
      handleFetchReport();
    }
    // Reset the flag after a short delay
    if (isAdjustingDateRange.current) {
      setTimeout(() => {
        isAdjustingDateRange.current = false;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.from, dateRange.to, includedPlatforms]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(value);
  };

  const reportData = data;
  const periodTotal = reportData?.periodTotal || 0;
  const totalDeliveredOrders =
    reportData?.totalDeliveredOrders || reportData?.totalOrders || 0;
  const totalCancelledOrders = reportData?.totalCancelledOrders || 0;
  const dailyTotals = reportData?.dailyTotals || {};
  const dailyOrderCounts = reportData?.dailyOrderCounts || {};
  const dailyCancelledCounts = reportData?.dailyCancelledCounts || {};

  const totalDays = Object.keys(dailyTotals).length;
  const averageDaily = totalDays > 0 ? periodTotal / totalDays : 0;

  // Calculate the last date with actual data
  const lastDateWithData = useMemo(() => {
    const datesWithData = Object.keys(dailyTotals).filter(
      (date) => {
        const revenue = dailyTotals[date] || 0;
        const orders = dailyOrderCounts[date] || 0;
        return revenue > 0 || orders > 0;
      }
    );

    if (datesWithData.length === 0) return null;

    const sortedDates = datesWithData.sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
    return sortedDates[sortedDates.length - 1];
  }, [dailyTotals, dailyOrderCounts]);

  // Adjust end date to the last day with data
  useEffect(() => {
    if (lastDateWithData && lastDateWithData < dateRange.to) {
      // Only update if the last date with data is earlier than the current end date
      isAdjustingDateRange.current = true;
      setDateRange((prev) => ({
        ...prev,
        to: lastDateWithData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDateWithData]);

  // Prepare chart data - only include dates with actual data
  const chartData = useMemo(() => {
    return Object.entries(dailyTotals)
      .filter(([date, revenue]: [string, any]) => {
        // Only include dates with actual data
        const hasRevenue = (revenue || 0) > 0;
        const hasOrders = (dailyOrderCounts[date] || 0) > 0;
        // Also filter by date range if lastDateWithData is set
        if (lastDateWithData && date > lastDateWithData) {
          return false;
        }
        return hasRevenue || hasOrders;
      })
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([date, revenue]: [string, any]) => ({
        date: format(new Date(date), 'MMM d'),
        fullDate: date,
        revenue: revenue || 0,
        orders: dailyOrderCounts[date] || 0,
      }));
  }, [dailyTotals, dailyOrderCounts, lastDateWithData]);


  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate('pos.reports_orders.name')}
          </h1>
          <p className="text-muted-foreground">
            {translate('pos.orders_report.subtitle')}
          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          {translate('pos.orders_report.export_report')}
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {translate('pos.orders_report.select_date_range')}
          </CardTitle>
          <CardDescription>
            {translate('pos.orders_report.choose_period')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Preset Selector */}
            <QuickDateRange
              selectedPreset={selectedPreset}
              onRangeChange={handleRangeChange}
            />

            {/* Date Range Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="from">
                  {translate('pos.orders_report.from_date')}
                </Label>
                <Input
                  id="from"
                  name="from"
                  type="date"
                  value={dateRange.from}
                  onChange={handleDateChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="to">
                  {translate('pos.orders_report.to_date')}
                </Label>
                <Input
                  id="to"
                  name="to"
                  type="date"
                  value={dateRange.to}
                  onChange={handleDateChange}
                />
              </div>
              <Button
                onClick={handleFetchReport}
                disabled={isLoading}
                className="h-10 gap-2"
              >
                {isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <TrendingUp className="h-4 w-4" />
                )}
                {isLoading
                  ? 'Loading...'
                  : translate('pos.orders_report.generate_report')}
              </Button>
            </div>

            {/* Platform Filter */}
            <div className="space-y-2 pt-4 border-t">
              <Label>
                {translate('pos.orders_report.platform_filter', 'Include Platforms')}
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Checkbox
                  id="platform-internal"
                  checked={includedPlatforms.has('internal')}
                  onChange={(checked) => {
                    const newSet = new Set(includedPlatforms);
                    if (checked) {
                      newSet.add('internal');
                    } else {
                      newSet.delete('internal');
                    }
                    setIncludedPlatforms(newSet);
                  }}
                  label={translate('pos.orders.deliveryPlatforms.internal', 'Internal')}
                />
                <Checkbox
                  id="platform-grab"
                  checked={includedPlatforms.has('grab')}
                  onChange={(checked) => {
                    const newSet = new Set(includedPlatforms);
                    if (checked) {
                      newSet.add('grab');
                    } else {
                      newSet.delete('grab');
                    }
                    setIncludedPlatforms(newSet);
                  }}
                  label={translate('pos.orders.deliveryPlatforms.grab', 'Grab')}
                />
                <Checkbox
                  id="platform-shopee"
                  checked={includedPlatforms.has('shopee')}
                  onChange={(checked) => {
                    const newSet = new Set(includedPlatforms);
                    if (checked) {
                      newSet.add('shopee');
                    } else {
                      newSet.delete('shopee');
                    }
                    setIncludedPlatforms(newSet);
                  }}
                  label={translate('pos.orders.deliveryPlatforms.shopee', 'Shopee')}
                />
                <Checkbox
                  id="platform-xanhsm"
                  checked={includedPlatforms.has('xanhsm')}
                  onChange={(checked) => {
                    const newSet = new Set(includedPlatforms);
                    if (checked) {
                      newSet.add('xanhsm');
                    } else {
                      newSet.delete('xanhsm');
                    }
                    setIncludedPlatforms(newSet);
                  }}
                  label={translate('pos.orders.deliveryPlatforms.xanhsm', 'Xanh SM')}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <TrendingUp className="h-6 w-6 text-primary animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate('pos.orders_report.loading_report')}
                </h3>
                <p className="text-muted-foreground">
                  {translate('pos.orders_report.fetching_data')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {isError && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <TrendingUp className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate('pos.orders_report.failed_to_load')}
                </h3>
                <p className="text-muted-foreground">
                  {translate('pos.orders_report.try_again_message')}
                </p>
              </div>
              <Button onClick={handleFetchReport} variant="outline">
                {translate('pos.orders_report.try_again')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Results */}
      {reportData && !isLoading && !isError && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders_report.total_revenue')}
                  </span>
                </div>
                <div className="text-2xl font-bold">
                  <span data-sensitive-field="amount">
                    {formatCurrency(periodTotal)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {totalDays}{' '}
                  {translate('pos.orders_report.days_period', {
                    count: totalDays,
                  })}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders_report.total_orders')}
                  </span>
                </div>
                <div className="text-2xl font-bold">{totalDeliveredOrders}</div>
                <p className="text-xs text-muted-foreground">
                  {totalDeliveredOrders > 0 && (
                    <>
                      {translate(
                        'pos.orders_report.delivered_orders'
                      )}
                      : {totalDeliveredOrders}
                      {totalCancelledOrders > 0 && (
                        <>
                          {' '}
                          •{' '}
                          {translate(
                            'pos.orders_report.cancelled_orders'
                          )}
                          : {totalCancelledOrders}
                        </>
                      )}
                    </>
                  )}
                  {totalDeliveredOrders === 0 &&
                    totalCancelledOrders === 0 &&
                    translate(
                      'pos.orders_report.total_orders_in_period'
                    )}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders_report.average_daily')}
                  </span>
                </div>
                <div className="text-2xl font-bold">
                  <span data-sensitive-field="amount">
                    {formatCurrency(averageDaily)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {translate('pos.orders_report.per_day_average')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {translate('pos.orders_report.revenue_chart')}
                </CardTitle>
                <CardDescription>
                  {translate(
                    'pos.orders_report.revenue_chart_description'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        yAxisId="revenue"
                        orientation="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          if (value >= 1000000) {
                            return `${(value / 1000000).toFixed(1)}M`;
                          }
                          if (value >= 1000) {
                            return `${(value / 1000).toFixed(1)}K`;
                          }
                          return value.toString();
                        }}
                      />
                      <YAxis
                        yAxisId="orders"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                        }}
                        formatter={(value: any, name: string) => {
                          if (name === 'revenue') {
                            return formatCurrency(value);
                          }
                          return value;
                        }}
                        labelFormatter={(label) => {
                          const dataPoint = chartData.find(
                            (d) => d.date === label
                          );
                          if (dataPoint) {
                            const dateLocale = locale === 'vi' ? vi : undefined;
                            return format(
                              new Date(dataPoint.fullDate),
                              'EEEE, MMMM d, yyyy',
                              dateLocale ? { locale: dateLocale } : undefined
                            );
                          }
                          return label;
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          if (value === 'revenue') {
                            return translate(
                              'pos.orders_report.revenue'
                            );
                          }
                          if (value === 'orders') {
                            return translate(
                              'pos.orders_report.orders'
                            );
                          }
                          return value;
                        }}
                      />
                      <Line
                        yAxisId="revenue"
                        type="monotone"
                        dataKey="revenue"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name="revenue"
                      />
                      <Line
                        yAxisId="orders"
                        type="monotone"
                        dataKey="orders"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name="orders"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.orders_report.daily_breakdown')}
              </CardTitle>
              <CardDescription>
                {translate(
                  'pos.orders_report.revenue_breakdown_description'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(dailyTotals).length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">
                    {translate('pos.orders_report.no_orders_found')}
                  </h3>
                  <p className="text-muted-foreground">
                    {translate('pos.orders_report.no_orders_message')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(dailyTotals)
                    .filter(([date, dailyData]: [string, any]) => {
                      // Only show dates with actual data
                      const hasRevenue = (dailyData || 0) > 0;
                      const hasOrders = (dailyOrderCounts[date] || 0) > 0;
                      // Also filter by date range if lastDateWithData is set
                      if (lastDateWithData && date > lastDateWithData) {
                        return false;
                      }
                      return hasRevenue || hasOrders;
                    })
                    .sort(
                      ([a], [b]) =>
                        new Date(a).getTime() - new Date(b).getTime()
                    )
                    .map(([date, dailyData]: [string, any]) => (
                      <div
                        key={date}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {format(
                                new Date(date),
                                'EEEE, MMMM d, yyyy',
                                locale === 'vi' ? { locale: vi } : undefined
                              )}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {format(
                                new Date(date),
                                'MMM d',
                                locale === 'vi' ? { locale: vi } : undefined
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col items-end">
                            <Badge variant="secondary" className="mb-1">
                              <span data-sensitive-field="amount">
                                {formatCurrency(dailyData)}
                              </span>
                            </Badge>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs text-muted-foreground">
                                {dailyOrderCounts[date] || 0}{' '}
                                {translate(
                                  'pos.orders_report.delivered_orders'
                                )}
                              </span>
                              {(dailyCancelledCounts[date] || 0) > 0 && (
                                <span className="text-xs text-muted-foreground text-destructive">
                                  {dailyCancelledCounts[date] || 0}{' '}
                                  {translate(
                                    'pos.orders_report.cancelled_orders'
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModernOrdersReport;
